import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';

import usePageContext from '@rover/react-lib/src/context/usePageContext';
import { useApiBrightHorizonsGlobalBannerRetrieve } from '@rover/rsdk/src/apiClient/latest';
import { DateRangeField } from '@rover/types/src/datetime/DateRange';

import useIsBrightHorizonsNavigation from './useIsBrightHorizonsNavigation';

const DEFAULT_BH_NAVBAR_OFFSET = '60px';

type UseBrightHorizonsNavBarResizeReturnType = {
  navBarRef: React.MutableRefObject<HTMLElement | null>;
  navBarPaddingOffset: string;
};

type UseBrightHorizonsNavBarResizeProps = {
  startDate: DateRangeField;
};

export const useBrightHorizonsNavBarResize = ({
  startDate,
}: UseBrightHorizonsNavBarResizeProps): UseBrightHorizonsNavBarResizeReturnType => {
  const isBrightHorizonsNavigation = useIsBrightHorizonsNavigation();
  const [navBarPaddingOffset, setNavBarPaddingOffset] = useState(
    isBrightHorizonsNavigation ? DEFAULT_BH_NAVBAR_OFFSET : '0px'
  );
  const navBarRef = useRef<HTMLElement | null>(null);
  const formattedStartDate = useMemo(() => {
    return startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
  }, [startDate]);
  // We need global banner response to recalculate height in the useEffect
  const { pageName } = usePageContext();

  const { data: bhGlobalBannerData } = useApiBrightHorizonsGlobalBannerRetrieve(
    {
      isBhHub: pageName === 'ssr-bh-hub',
      startDate: formattedStartDate,
    },
    { query: { enabled: isBrightHorizonsNavigation } }
  );

  useEffect(() => {
    let animationFrameId: number;

    const handleResize = (): void => {
      if (!isBrightHorizonsNavigation) return;
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(() => {
        if (navBarRef.current) {
          setNavBarPaddingOffset(`${navBarRef.current.offsetHeight}px`);
        }
      });
    };

    // Get initial offset
    handleResize();

    // Recalculate offset on resize
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [bhGlobalBannerData, isBrightHorizonsNavigation]);

  return {
    navBarRef,
    navBarPaddingOffset,
  };
};
